// Authentication utility functions
// Centralized functions for handling authentication operations like logout on token expiry
import { API_CONFIG } from '../config/api.js';
import authService from '../services/authService.js';
import { getLocalStorage } from './browserCheck.js';

// Flag to prevent multiple concurrent logout calls
let isLogoutInProgress = false;

/**
 * Handles token expiry by clearing auth data and redirecting to login
 * This function should be called whenever a 401 error is detected
 * @param {string} reason - Optional reason for logout (for logging)
 */
export const handleTokenExpiry = async (reason = 'Token expired') => {
  // If logout is already in progress, skip to avoid multiple logout API calls
  if (isLogoutInProgress) {
    console.log(`⏭️ [authUtils] Logout already in progress, skipping duplicate call: ${reason}`);
    return;
  }

  // Set flag to prevent concurrent calls
  isLogoutInProgress = true;
  console.warn(`🚨 [authUtils] Token expiry detected: ${reason}`);
  
  try {
    // Get user data before clearing to determine logout endpoint
    const userData = authService.getUser();
    const isHospitalUser = userData && (userData.role === 'HOSPITAL' || userData.role === 'HOSPITAL_USER' || userData.role === 'hospital');
    const isFleetUser = userData && (userData.role === 'TENANT_ADMIN' || userData.role === 'TENANT_USER' || userData.role === 'fleet');

    // Try to call appropriate logout endpoint (non-blocking)
    // Only make one logout API call even if multiple 401s occur
    try {
      if (isHospitalUser) {
        await authService.hospitalLogout();
      } else if (isFleetUser) {
        await authService.fleetLogout();
      } else {
        await authService.logout();
      }
    } catch (logoutError) {
      // Logout endpoint call failed, but continue with clearing local data
      console.warn('⚠️ [authUtils] Logout endpoint call failed, but clearing local data:', logoutError);
    }
  } catch (error) {
    console.warn('⚠️ [authUtils] Error during logout process:', error);
  } finally {
    // Always clear local storage regardless of logout endpoint result
    // Check if we're in a browser environment
    const storage = getLocalStorage();
    if (storage) {
      storage.removeItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
      storage.removeItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
      storage.removeItem(API_CONFIG.STORAGE_KEYS.USER_DATA);
      storage.removeItem('token'); // Legacy key
      storage.removeItem('user'); // Legacy key
      storage.removeItem('aps_hospital_id'); // Hospital-specific key
      storage.removeItem('aps_hospital_data'); // Hospital-specific key
    }
    
    // Redirect to login page (only in browser)
    if (typeof window !== 'undefined') {
      console.log('🔐 [authUtils] Redirecting to login page...');
      window.location.href = '/login';
    }
  }
};

/**
 * Checks if a JWT token is expired by decoding it
 * @param {string} token - The JWT token to check
 * @returns {boolean} - True if token is expired or invalid, false otherwise
 */
export const isTokenExpired = (token) => {
  if (!token || typeof token !== 'string') {
    return true;
  }

  try {
    // JWT tokens have three parts: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('⚠️ [authUtils] Invalid JWT token format');
      return true;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = JSON.parse(atob(paddedPayload));

    // Check if token has expiration claim
    if (!decodedPayload.exp) {
      // No expiration claim, assume token is valid (but this is unusual)
      console.warn('⚠️ [authUtils] Token has no expiration claim');
      return false;
    }

    // Check if token is expired
    // exp is in seconds, Date.now() is in milliseconds
    const expirationTime = decodedPayload.exp * 1000;
    const currentTime = Date.now();
    const isExpired = currentTime >= expirationTime;

    if (isExpired) {
      console.warn('🚨 [authUtils] Token is expired');
    }

    return isExpired;
  } catch (error) {
    console.error('❌ [authUtils] Error checking token expiration:', error);
    // If we can't decode the token, assume it's invalid/expired
    return true;
  }
};

/**
 * Checks if an endpoint should skip auto-logout on 401
 * Only login/register endpoints should skip logout
 * @param {string} url - The request URL
 * @returns {boolean} - True if logout should be skipped
 */
export const shouldSkipAutoLogout = (url) => {
  if (!url) return false;
  
  // Only skip logout for actual authentication endpoints (login, register, OTP)
  const skipEndpoints = [
    '/login',
    '/register',
    '/hospital/register',
    '/auth/admin/register-fleet-operator',
    '/send-login-otp',
    '/complete-login',
    '/v1/hospital/auth/login',
    '/v1/fleet/auth/login',
    '/v1/hospital/auth/refresh-token',
    '/v1/fleet/auth/refresh-token',
  ];
  
  return skipEndpoints.some(endpoint => url.includes(endpoint));
};

